import { useCallback, useEffect, useState, useSyncExternalStore } from 'react';
import type { StreamChat } from 'stream-chat';
import type { ChatFileUploadResponse, ConversationDetailsDto } from '@chat-emp/shared';

import {
  EMPLOYER_ID,
  channels as baseChannels,
  messages as seed,
  participants,
} from '../fixtures/data.js';
import { CHAT_ENDED_NOTE } from '../fixtures/domain.js';
import type {
  Attachment,
  ApplicationStatus,
  CandidateProfile,
  Channel,
  ClosingVariant,
  CtaPayload,
  Form,
  InboxItem,
  JobCard,
  JobMeta,
  Message,
  MessageId,
  Participant,
} from '../fixtures/domain.js';
import { snapshotOf } from './forms.js';
import {
  isBounced,
  MessageBouncedError,
  toBounce,
  type Bounce,
  type SentMessage,
} from './moderation.js';
import {
  dispatchForm as dispatchFormApi,
  editMessage as editMessageApi,
  startChatFileUpload,
  endChat as endChatApi,
  linkFormDispatch as linkFormDispatchApi,
  getConversationDetails,
  sendJobApi,
  setApplicantStatus as setApplicantStatusApi,
} from '../lib/facade.js';
import { ApiRequestError } from '../lib/api.js';
import { cidParts, emitToHost } from '../lib/bridge.js';
import { uploadName } from '../conversation/attachment-rules.js';
import { firstReplyEmitDetail } from '../lib/gamification.js';
import { track } from '../lib/analytics.js';
import type { HiringStatusWrite } from '@chat-emp/shared';
import {
  applicationStatusOf,
  channelCategory,
  isEnded,
  needsReply,
  sortByRecentActivity,
  type ChannelCategory,
} from '../fixtures/rules.js';
import {
  hydrateChannels,
  mapChannel,
  mapMessages,
  queryUserChannels,
  setUnlockHint,
  type MappedChannel,
} from './stream.js';
import { isEmployerSide } from '@chat-emp/shared';

/**
 * THE SEAM (the single data source for the UI-only build). Holds the base
 * fixtures plus session mutations — messages sent,
 * and the hiring-status / ended overlays — behind one subscribe API. Every
 * surface (ChatView, the widget) reads and writes here through the hooks below,
 * so the re-wiring epic swaps this module's internals (fixtures → BFF/Stream)
 * without touching a single component.
 */
interface Mutations {
  outbox: Record<string, Message[]>;
  statuses: Record<string, ApplicationStatus>;
  ended: Record<string, boolean>;
}

let mut: Mutations = { outbox: {}, statuses: {}, ended: {} };
let version = 0;
const listeners = new Set<() => void>();

function bump(): void {
  version += 1;
  for (const listener of listeners) listener();
}

function commit(next: Mutations): void {
  mut = next;
  bump();
}

function subscribe(onChange: () => void): () => void {
  listeners.add(onChange);
  return () => {
    listeners.delete(onChange);
  };
}

// ── live (Stream) source ─────────────────────────────────────────────────────
// When a connected client is installed the seam reads real watched channels
// instead of fixtures. The channels are watched (`watch:true`), so their state
// mutates in place on Stream events — we just re-map on read and bump `version`
// so React re-reads. Local fixtures mutations (outbox/statuses/ended) are
// fixtures-mode only; live mode's source of truth is the channels themselves.
interface Live {
  client: StreamChat;
  channels: MappedChannel['raw'][];
  off: () => void;
}
let live: Live | null = null;
let liveLoading = false;
// The initial channel query failed — surfaced so the auth gate can show an
// error/retry screen instead of holding "Connecting…" forever.
let liveError = false;
// The last client handed to installLiveClient, kept so retryLiveInstall can
// re-run the failed query without the bridge re-mounting.
let lastClient: StreamChat | null = null;
// Bumped by every install and by clearLiveClient, so an in-flight query can tell
// it was superseded/torn down while awaiting and bail before mutating `live`.
let liveGeneration = 0;

// `message.new` is handled on its own (it also drives `reply_received`); the rest
// just re-map + re-render.
const LIVE_EVENTS = [
  'message.updated',
  'message.deleted',
  'notification.mark_read',
  'channel.updated',
] as const;

// Receipts move on the candidate's progress alone. Both events also fire for the
// employer's own reads, which can never change a tick we draw, and their effect
// on our own unread count already arrives as `notification.mark_read` above.
// `message.delivered` only fires at all where the channel type has delivery
// events enabled.
const RECEIPT_EVENTS = ['message.read', 'message.delivered'] as const;

// Channels the employer has sent in this session — the gate for `reply_received`
// (an inbound message only counts as a reply once the employer has replied there).
// Cleared with the live client so a new session starts fresh.
const sentChannels = new Set<string>();

// Each chat's unacknowledged bounce, by cacheKey. Stream never stores a bounced message, so this tab is its only home.
const bounces = new Map<string, Bounce>();

/**
 * How many times the backend has told us a channel's `/details` went stale.
 *
 * The counter is the whole mechanism: `useConversationDetails` reads it as an
 * effect dependency, so incrementing it re-runs the fetch. A counter rather
 * than a boolean because two announcements in a row must both be heard — a
 * flag set twice looks identical to a flag set once.
 *
 * Keyed by channel so an announcement about a conversation nobody has open
 * costs one map entry and no fetch; the panel picks it up whenever it opens,
 * which it would have done anyway.
 */
const detailsRevisions = new Map<string, number>();

function bumpDetails(channelId: string): void {
  detailsRevisions.set(channelId, (detailsRevisions.get(channelId) ?? 0) + 1);
  bump();
}

/** Install a connected client: query the user's channels once, then keep the
 *  mapped-channel cache fresh off live events. Awaited, so `live` is populated
 *  before the first render that reads it. */
export async function installLiveClient(
  client: StreamChat,
  { preload = false }: { preload?: boolean } = {},
): Promise<void> {
  const generation = ++liveGeneration;
  lastClient = client;
  liveLoading = true;
  liveError = false;
  bump();
  try {
    // `preload` pulls every channel the account has, and no caller asks for it
    // any more: the widget's list moved to the read model, which is where /chat's
    // already was. Both select through the query and hydrate the one channel a
    // conversation shows, so the unbounded query is pure cost on either surface.
    // Kept for the seam's own tests — see LiveDataBridge.
    const mapped = preload ? await queryUserChannels(client) : [];
    // Superseded by a clear or a newer install while the query was in flight:
    // don't register listeners or install a torn-down client (would leak the
    // `off` closure and resurrect `live` on a dead connection).
    if (generation !== liveGeneration) return;
    const subs = LIVE_EVENTS.map((e) => client.on(e, () => bump()));
    for (const e of RECEIPT_EVENTS) {
      subs.push(
        client.on(e, (event) => {
          if (event.user?.id !== client.userID) bump();
        }),
      );
    }
    // message.new re-renders AND drives `reply_received`: an inbound message on a
    // channel the employer has already replied in. The employer's own sends mark
    // the channel as replied-in.
    subs.push(
      client.on('message.new', (event) => {
        bump();
        const channelId = event.channel_id;
        if (!channelId) return;
        if (isEmployerSide(event.user?.id ?? '')) sentChannels.add(channelId);
        else if (sentChannels.has(channelId)) track('reply_received', { channel_id: channelId });
      }),
    );
    // Cache invalidation over the socket we already hold: the backend announces
    // that a read went stale, and the panel re-reads through its own authorized
    // route. The event carries a resource name and never the data itself —
    // it reaches BOTH sides of the channel, while the facts behind it (an
    // employer's unlock, say) are per-viewer.
    //
    // Unknown resources are ignored on purpose, so the backend can start
    // announcing something new before this client knows what it is.
    //
    // No dot in the event name: Stream keeps the dotted namespace for its own
    // built-in events and rejects a custom type carrying one. The resource
    // below may have a dot — that restriction is on the type alone.
    subs.push(
      client.on('data_changed', (event) => {
        // Cast because `resource` is our own field on a custom event, and the
        // SDK's Event type only knows Stream's own. Read as unknown and
        // compared, never trusted into a lookup.
        const { resource } = event as { resource?: unknown };
        if (resource !== 'conversation.details') return;
        if (event.channel_id) bumpDetails(event.channel_id);
      }),
    );
    // The end of the end-chat funnel: the channel was hard-deleted after its
    // countdown. Re-map so it drops out of the lists, and report it — this is the
    // only signal that a closed chat actually went away.
    subs.push(
      client.on('channel.deleted', (event) => {
        bump();
        if (event.channel_id) {
          track('end_chat_channel_deleted', { channel_id: event.channel_id });
        }
      }),
    );
    // The header's online dot. The SDK has already patched the member's
    // `online` (presence is on in hydrateChannels); a re-render re-maps it. Only
    // for someone in an open conversation — the inbox list has its own listener
    // (inbox.ts), and a bump re-renders every reader of the seam.
    subs.push(
      client.on('user.presence.changed', (event) => {
        const id = event.user?.id;
        if (!id || id === client.userID) return;
        if ([...openChannels.values()].some((raw) => raw.state.members[id])) bump();
      }),
    );
    // A brand-new channel isn't in the watched set — re-query to pick it up.
    const added = client.on('notification.added_to_channel', () => void reloadLive());
    live = {
      client,
      channels: mapped.map((m) => m.raw),
      off: () => {
        for (const s of subs) s.unsubscribe();
        added.unsubscribe();
      },
    };
  } catch {
    // A failed initial query must not escape as an unhandled rejection (the
    // bridge calls this fire-and-forget). Surface it as an error state instead
    // — unless a clear/newer install superseded this one and owns the gate.
    if (generation === liveGeneration) liveError = true;
  } finally {
    // Only clear the loading flag if we still own this generation; a newer
    // install (or a clear) owns it otherwise.
    if (generation === liveGeneration) {
      liveLoading = false;
      bump();
    }
  }
}

/** Re-run the initial query after a failure (the auth gate's Retry). */
export async function retryLiveInstall(): Promise<void> {
  if (!lastClient || !liveError) return;
  await installLiveClient(lastClient);
}

async function reloadLive(): Promise<void> {
  const generation = liveGeneration;
  if (!live) return;
  let mapped: MappedChannel[];
  try {
    mapped = await queryUserChannels(live.client);
  } catch {
    // A failed refresh keeps the already-loaded channels: stale-but-working
    // beats tearing the UI down to an error screen mid-conversation.
    return;
  }
  // Cleared or replaced during the query — don't write to a stale/null `live`.
  if (generation !== liveGeneration || !live) return;
  live.channels = mapped.map((m) => m.raw);
  bump();
}

/** Tear down the live source (widget closed / view unmounted). */
export function clearLiveClient(): void {
  liveGeneration++;
  openChannels.clear();
  openingChannels.clear();
  missingChannels.clear();
  live?.off();
  live = null;
  liveLoading = false;
  liveError = false;
  lastClient = null;
  sentChannels.clear();
  bounces.clear();
  bump();
}

// ── reads ────────────────────────────────────────────────────────────────────
const byId = new Map(participants.map((p) => [p.id, p]));

function allChannels(): Channel[] {
  return baseChannels;
}

/** Whether an id names a real channel — used to validate the widget deep-link.
 *  In live mode the ids are Stream `cid`s (what the widget's expand button
 *  passes), so consult the live set; otherwise the fixtures. */
export function hasChannel(id: string): boolean {
  // Live mode cannot answer this without a round trip, and answering "no" from
  // a partially-loaded set is what made deep links to older conversations land
  // on the list instead of the chat. Accept it and let the pane hydrate: if the
  // channel really is gone, it renders its own empty state.
  if (live) return true;
  return allChannels()
    .map((c) => c.id)
    .includes(id);
}

/** Whether a chat still exists, asked of Stream once when a form response is
 *  opened: a chat hard-deleted by retention leaves its answers behind. */
export async function channelExists(cid: string): Promise<boolean> {
  if (!live) return hasChannel(cid);
  const found = await live.client.queryChannels(
    { cid },
    {},
    { limit: 1, message_limit: 0, watch: false, state: false, presence: false },
  );
  return found.length > 0;
}

/**
 * Send one message to a channel by cid, for bulk send and for Chat Day invites.
 *
 * Deliberately NOT `sendLive`: that path runs on a *watched* channel and fires
 * the first-reply reward, the host `message-sent` emit, and per-message
 * analytics — all wrong to replay N times for a bulk blast (reward farming,
 * host spam). The message itself is identical to a typed one; the per-message
 * side-effects stay with the single-send path. Live mode only — throws when no
 * client is connected, so the caller counts it as a failure rather than
 * crashing the batch.
 *
 * `cta` rides as Stream's custom top-level field, which is where every other
 * author of a button row already puts it and what `data/stream.ts` reads back.
 * Written on the way out exactly as it will be read on the way in — the shape
 * is flat and lowercase throughout, so nothing has to be re-cased here.
 */
export async function sendMessageTo(
  channelId: string,
  text: string,
  cta?: CtaPayload,
): Promise<void> {
  if (!lastClient) throw new Error('bulk send unavailable: no live client');
  const { channelType, channelId: id } = cidParts(channelId);
  await lastClient.channel(channelType, id).sendMessage({ text, ...(cta ? { cta } : {}) });
  // Like sendLive, bump the version so the inbox re-reads (last-message preview,
  // sort order) off the freshly-mutated channel — not on a later websocket echo.
  bump();
}

/**
 * A form went out (AI-3014). Fired from both send paths so "forms sent per day"
 * is one count rather than a sum of two.
 *
 * `dispatch_id` is the point of the event, not a detail: chat-kit pins a
 * dispatch per channel, so it is the only id that tells two sends of the same
 * form apart — and so the only one a fill event can join back on. `form_id`
 * rides along for "which forms get used", which `dispatch_id` cannot answer.
 */
function trackFormSent(channelId: string, formId: string, dispatchId: string, bulk: boolean): void {
  track('form_sent', {
    channel_id: channelId,
    form_id: formId,
    dispatch_id: dispatchId,
    bulk,
  });
}

/**
 * Send one form to a channel by cid, for bulk send. The thread's `sendForm`
 * with `sendMessageTo`'s restraint: the same dispatch → post → link sequence,
 * on the unwatched-channel path with none of the per-message side-effects
 * (reward, host emit, `message_sent`) that would be wrong replayed N times.
 *
 * `form_sent` IS fired per target, unlike those. A form's send has to be
 * joinable to its fill one dispatch at a time (AI-3014), and `bulk_message_sent`
 * counts batches — so one event per form is the unit, not one per batch.
 *
 * A dispatch is per-channel — chat-kit pins the form's version against the chat
 * it goes to — so bulk means one dispatch per target, never one shared across
 * the set. `note` is the form card's own note, exactly as the thread sends it:
 * one message, never a message and then a card behind it.
 */
export async function sendFormTo(channelId: string, note: string, form: Form): Promise<void> {
  if (!lastClient) throw new Error('bulk send unavailable: no live client');
  const dispatch = await dispatchFormApi({ formId: form.id, channelId });
  const { channelType, channelId: id } = cidParts(channelId);
  // The spread, as in sendLive: `form` is a custom top-level field, which the
  // SDK's message type only admits past the excess-property check this way.
  const res = (await lastClient
    .channel(channelType, id)
    .sendMessage({ text: note.trim(), ...{ form: dispatch.notice } })) as
    { message?: { id?: string } } | undefined;
  bump();
  // Best effort, as in the thread: the notice already rides the message, and an
  // unlinked dispatch is chat-kit's documented repair case, not a failed send.
  const messageId = res?.message?.id;
  if (messageId) {
    void linkFormDispatchApi({ dispatchId: dispatch.dispatchId, messageId }).catch(() => undefined);
  }
  trackFormSent(id, form.id, dispatch.dispatchId, true);
}

/**
 * A channel with this session's mutations folded in: the hiring-status and ended
 * overlays, plus the last sent message reflected as the channel's last-message /
 * activity with a cleared unreplied count. Applied BEFORE categorizing, sorting,
 * and counting so the inbox, the Ended list, and the pending-reply badge stay
 * consistent with what the conversation pane reads off the same seam.
 */
function withOverlays(channel: Channel): Channel {
  const status = mut.statuses[channel.id];
  const ended = mut.ended[channel.id];
  const out = mut.outbox[channel.id];
  const last = out && out.length > 0 ? out[out.length - 1] : undefined;
  if (!status && !ended && !last) return channel;
  return {
    ...channel,
    ...(status ? { applicationStatus: status } : {}),
    ...(ended ? { phase: 'closing' as const } : {}),
    ...(last
      ? {
          // A form sent with no note previews as the form's name.
          lastMessageText: last.text || (last.form?.name ?? ''),
          lastActivityAt: last.createdAt,
          lastSenderId: last.senderId,
          // The employer sent last, so nothing is awaiting their reply.
          unrepliedCount: 0,
          firstReplyPending: false,
        }
      : {}),
  };
}

// ── the open conversation's channel ──────────────────────────────────────────
/**
 * The conversation pane hydrates the one channel it is showing, by id, instead
 * of finding it in a pre-loaded list of every channel. That is what lets a chat
 * be opened when it is not on the first page — including a `?channel=` deep
 * link, which used to fail silently for older conversations.
 *
 * Closing a pane only drops the cache entry — the channel stays watched for the
 * session, and `disconnectUser` ends every watch server-side when chat closes.
 * (Per-channel `stopWatching` is deliberately unused: it costs one HTTP request
 * per channel and page-sized bursts of it hit Stream's rate limit — AI-2550.)
 */
const openChannels = new Map<string, MappedChannel['raw']>();
/**
 * Hydrations in flight — the promise itself, not just the id, so a second caller
 * can await the same open. See `openConversationChannel`.
 */
const openingChannels = new Map<string, Promise<void>>();
/** Ids we hydrated and found nothing for — a deleted or inaccessible channel. */
const missingChannels = new Set<string>();

/** Stream's `$in` filter takes the bare id; our Channel.id is the cid. */
function bareId(cid: string): string {
  const i = cid.indexOf(':');
  return i === -1 ? cid : cid.slice(i + 1);
}

/**
 * The key every cache above is held under: the bare id, never the raw string.
 *
 * Both forms legitimately reach these functions. `useConversation` is handed a
 * `Channel.id`, which `mapChannel` always fills from Stream's cid
 * (`messaging:chat-comp-…`); `useConversationRow` is handed whatever the caller
 * holds, and for a host's View/Reply that is the canonical `chat-comp-…`. Keyed
 * raw, those two are different channels: the same conversation hydrates twice,
 * the second request cannot see the first one's entry, and only one of the two
 * is ever freed — the other rides out the session as a watched channel nothing
 * points at. Normalising here is what makes "already open" and "already opening"
 * true answers rather than answers about spelling.
 */
function cacheKey(cid: string): string {
  return bareId(cid);
}

/**
 * Hydrate one conversation's channel into `openChannels`, or hand back the
 * hydration already running for it.
 *
 * Awaiting the result means the channel is in `openChannels` — that is the whole
 * contract, and it used to be broken: a second caller arriving while the first
 * was still fetching got an already-resolved promise back, so `.then(...)` ran
 * before the channel existed. `markReadLive` is exactly that caller, which is why
 * opening a conversation never actually reported it read.
 */
function openConversationChannel(cid: string): Promise<void> {
  const key = cacheKey(cid);
  if (!live || openChannels.has(key)) return Promise.resolve();

  const inflight = openingChannels.get(key);
  if (inflight) return inflight;

  const { client } = live;
  const opening = (async () => {
    try {
      const [mapped] = await hydrateChannels(client, [key]);
      if (mapped) {
        openChannels.set(key, mapped.raw);
        missingChannels.delete(key);
      } else {
        missingChannels.add(key);
      }
    } catch {
      // Leave it unhydrated; the pane shows its empty state and reopening retries.
    } finally {
      openingChannels.delete(key);
      bump();
    }
  })();

  openingChannels.set(key, opening);
  bump();
  return opening;
}

function closeConversationChannel(cid: string): void {
  openChannels.delete(cacheKey(cid));
}

interface SeamRow {
  channel: Channel;
  counterpart: Participant;
}

/**
 * The channels the seam currently exposes, each with its counterpart: live
 * watched channels when a client is installed, otherwise fixtures with session
 * overlays folded in. Both modes flow through the same pure rules downstream.
 */
function currentRows(): SeamRow[] {
  if (live) {
    const uid = live.client.userID ?? '';
    return live.channels.map((raw) => {
      const m = mapChannel(raw, uid);
      // Fold the optimistic hiring-status overlay (persistLiveStatus sets it) so
      // the inbox row reflects a just-set stage too, matching the conversation
      // header — not only the header, until a channel event lands.
      const status = mut.statuses[m.channel.id];
      const channel = status ? { ...m.channel, applicationStatus: status } : m.channel;
      return { channel, counterpart: m.counterpart };
    });
  }
  return allChannels().map((c) => ({
    channel: withOverlays(c),
    counterpart: byId.get(c.counterpartId)!,
  }));
}

// ── writes ───────────────────────────────────────────────────────────────────
function newMessage(
  channelId: string,
  text: string,
  system: boolean,
  quotedMessageId?: MessageId,
): Message {
  const count = mut.outbox[channelId]?.length ?? 0;
  return {
    id: `local-${channelId}-${count}${system ? '-note' : ''}`,
    channelId,
    senderId: EMPLOYER_ID,
    text,
    createdAt: new Date().toISOString(),
    status: 'sent',
    system,
    ...(quotedMessageId ? { quotedMessageId } : {}),
  };
}

function send(channelId: string, text: string, quotedMessageId?: MessageId): void {
  if (text.trim() === '') return;
  const message = newMessage(channelId, text, false, quotedMessageId);
  commit({
    ...mut,
    outbox: { ...mut.outbox, [channelId]: [...(mut.outbox[channelId] ?? []), message] },
  });
}

function sendFormFixture(channelId: string, form: Form, note: string): void {
  const message: Message = { ...newMessage(channelId, note.trim(), false), form: snapshotOf(form) };
  commit({
    ...mut,
    outbox: { ...mut.outbox, [channelId]: [...(mut.outbox[channelId] ?? []), message] },
  });
}

function sendJobFixture(channelId: string, jobId: string): void {
  const card: JobCard = { jobId, title: `Job posting ${jobId}`, jobType: 'Full Time' };
  const message: Message = { ...newMessage(channelId, card.title, false), job: card };
  commit({
    ...mut,
    outbox: { ...mut.outbox, [channelId]: [...(mut.outbox[channelId] ?? []), message] },
  });
}

function editFixture(channelId: string, messageId: MessageId, text: string): void {
  const edit = (m: Message) =>
    m.id === messageId ? { ...m, text, editedAt: new Date().toISOString() } : m;
  commit({
    ...mut,
    outbox: { ...mut.outbox, [channelId]: (mut.outbox[channelId] ?? []).map(edit) },
  });
}

function setApplicationStatus(channelId: string, status: ApplicationStatus): void {
  commit({ ...mut, statuses: { ...mut.statuses, [channelId]: status } });
}

/** Drop a session status override, revealing the channel's own status again. */
function clearStatusOverride(channelId: string): void {
  if (!(channelId in mut.statuses)) return;
  const next = { ...mut.statuses };
  delete next[channelId];
  commit({ ...mut, statuses: next });
}

/** The stages the split-button persists (reject flows through end-chat, not here). */
const WRITE_STATUSES: ReadonlySet<ApplicationStatus> = new Set([
  'shortlisted',
  'interview',
  'hired',
]);

/**
 * Live status write: apply the stage optimistically (as a session overlay the
 * conversation and inbox both read), persist it via the BFF, and roll back to
 * the prior overlay on failure so the UI never keeps a change the backend
 * rejected. ES becomes authoritative again on the next details fetch.
 */
/** Rejects once the overlay is back, so the caller can say the write failed.
 *  The rollback alone shows the stage snapping back with no reason given. */
async function persistLiveStatus(channelId: string, status: ApplicationStatus): Promise<void> {
  const previous = mut.statuses[channelId]; // undefined when there was no override
  setApplicationStatus(channelId, status);
  const { channelType, channelId: id } = cidParts(channelId);
  // stream-chat parity: the host learns of the hiring action (optimistic), and
  // of a rollback if the persist fails.
  emitToHost('sidebar-application-action', { channelId: id, channelType, status });
  // No `sidebar_action_taken` here: the BFF emits it server-side when the write
  // actually lands, so this optimistic overlay would double-count it (and would
  // count writes that roll back below).
  try {
    await setApplicantStatusApi(channelId, status as HiringStatusWrite);
  } catch (error) {
    emitToHost('sidebar-application-action-error', { channelId: id, channelType, status });
    if (previous === undefined) clearStatusOverride(channelId);
    else setApplicationStatus(channelId, previous);
    throw error;
  }
}

/** Toggle the optimistic "ended" overlay so the live conversation reads as ended
 *  the instant the employer confirms — before the backend's `closing` stamp
 *  round-trips through the watched channel. Cleared on failure to roll back. */
function setEndedOverlay(channelId: string, ended: boolean): void {
  if (ended) {
    commit({ ...mut, ended: { ...mut.ended, [channelId]: true } });
  } else if (mut.ended[channelId]) {
    const next = { ...mut.ended };
    delete next[channelId];
    commit({ ...mut, ended: next });
  }
}

/** End the chat: append the closing message + the "You ended this chat." system
 *  note, mark it ended, and (for reject) set the applicant rejected. */
function endWithMessage(channelId: string, text: string, variant: ClosingVariant): void {
  const closing = newMessage(channelId, text, false);
  const note = newMessage(channelId, CHAT_ENDED_NOTE, true);
  commit({
    ...mut,
    outbox: { ...mut.outbox, [channelId]: [...(mut.outbox[channelId] ?? []), closing, note] },
    ended: { ...mut.ended, [channelId]: true },
    statuses: variant === 'reject' ? { ...mut.statuses, [channelId]: 'rejected' } : mut.statuses,
  });
}

// ── hooks ────────────────────────────────────────────────────────────────────
function useSource(): void {
  useSyncExternalStore(subscribe, () => version);
}

/** Whether the seam has a connected client with its channels loaded — real data
 *  is ready to render (not fixtures, not mid-connect). The embed auth gate reads
 *  this so it can hold a connecting state instead of flashing fixtures during the
 *  connect/first-query window. */
export function useLiveReady(): boolean {
  useSource();
  return live !== null;
}

/** Whether the initial live channel query failed — the embed auth gate reads
 *  this to show an error/retry screen instead of holding "Connecting…". */
export function useLiveError(): boolean {
  useSource();
  return liveError;
}

const NOOP = async (): Promise<void> => {};

export interface UseInbox {
  lists: Record<ChannelCategory, InboxItem[]>;
  loading: boolean;
  reload: () => Promise<void>;
}

/**
 * `markRead` is a write, so a conversation already caught up on skips it.
 *
 * Stream's own count, deliberately, and the one place still asking it: the
 * question here is "has Stream anything left to acknowledge", not "is this
 * unread" — and Stream counts a system note that the inbox's own unread does
 * not. Reading the inbox's answer would leave the watermark parked behind a
 * notice, and with it the read event the read model advances on.
 */
async function markReadLive(channelId: string): Promise<void> {
  const raw =
    openChannels.get(cacheKey(channelId)) ?? live?.channels.find((c) => c.cid === channelId);
  if (!raw || raw.countUnread() === 0) return;
  await raw.markRead();
  bump();
}

/**
 * The open conversation as an inbox row — counterpart, unread, job meta.
 *
 * This is what lets the conversation header render for a chat that was never in
 * any list: a `?channel=` deep link, or a row from a rail the read model
 * selected. Returns null until hydration lands.
 *
 * It hydrates what it reads rather than only reading what `useConversation`
 * hydrated. On /chat the two are always mounted together, so the distinction
 * never showed; the widget resolves a host's View/Reply *before* there is a
 * thread to mount, so a row that waits for someone else to fetch it never
 * arrives. `openConversationChannel` dedups against the in-flight
 * open, so the pair still costs one round trip.
 *
 * Deliberately no cleanup: `useConversation` owns that lifecycle, and closing
 * from here would evict the channel out from under an open thread.
 */
export function useConversationRow(channelId: string | null): InboxItem | null {
  useSource();
  const liveReady = useLiveReady();

  // `liveReady` is what re-runs this once a client installs — same reason
  // `useConversation`'s read effect carries it. Without it, a row asked for
  // before the connect lands would hydrate against a null seam and stop there.
  useEffect(() => {
    if (!channelId) return;
    void openConversationChannel(channelId);
  }, [channelId, liveReady]);

  if (!channelId || !live) return null;
  const raw = openChannels.get(cacheKey(channelId));
  if (!raw) return null;
  const m = mapChannel(raw, live.client.userID ?? '');
  // Same optimistic hiring-status overlay currentRows folds in, so a just-set
  // stage shows here too rather than waiting for a channel event.
  const status = mut.statuses[m.channel.id];
  const channel = status ? { ...m.channel, applicationStatus: status } : m.channel;
  return { channel, counterpart: m.counterpart, needsReply: needsReply(channel) };
}

export function useInbox(): UseInbox {
  useSource();
  const rows = currentRows();
  const cpById = new Map(rows.map((r) => [r.channel.id, r.counterpart]));
  const items = sortByRecentActivity(rows.map((r) => r.channel)).map((channel): InboxItem => ({
    channel,
    counterpart: cpById.get(channel.id)!,
    needsReply: needsReply(channel),
  }));
  const lists = {
    inbox: items.filter((i) => channelCategory(i.channel) === 'inbox'),
    ended: items.filter((i) => channelCategory(i.channel) === 'ended'),
    broadcast: items.filter((i) => channelCategory(i.channel) === 'broadcast'),
  };
  return {
    lists,
    loading: liveLoading,
    reload: live ? reloadLive : NOOP,
  };
}

export interface UseConversation {
  messages: Message[];
  loading: boolean;
  sending: boolean;
  ended: boolean;
  applicationStatus: ApplicationStatus;
  /** This session's optimistic status override, if the employer just set one —
   *  distinct from the channel/ES status. Lets a consumer prefer the ES status
   *  as the base while still reflecting an in-flight action. */
  statusOverride?: ApplicationStatus;
  /** Rejects if the message did not reach Stream, so the composer can hand the
   *  text back rather than swallow it. Moderation runs upstream, so a refused
   *  send is a normal outcome, not only a network failure.
   *
   *  `quotedMessageId` answers a specific message. It is an id rather than the
   *  quoted text so an edit upstream is not frozen into the reply. */
  send: (
    text: string,
    quotedMessageId?: MessageId,
    attachments?: Attachment[],
    plain?: boolean,
  ) => Promise<string | undefined>;
  /**
   * Send a form into this chat: one employer message carrying the optional
   * note and a snapshot of the form frozen at send (see `FormNotice`). A real
   * message, so every send side effect (first-reply, preview) applies.
   */
  sendForm: (form: Form, note: string) => Promise<void>;
  /** Send a posting as a card: one message, text = the job title (fallback), `job` = the frozen snapshot. */
  /** One of the company's live postings, by id; the backend freezes and posts the card. */
  sendJob: (jobId: string) => Promise<void>;
  /** Replace the text of our own message. Rejects with a 409 once it is no longer editable. */
  editMessage: (messageId: MessageId, text: string) => Promise<void>;
  /** The latest send moderation bounced here and not yet acknowledged; one card however many. */
  bounce: Bounce | null;
  /** "I Understand": clears the card. The message was never sent, so nothing upstream changes. */
  dismissBounce: () => void;
  /**
   * Upload a file and hand back the reference to attach: our own bucket, or
   * Stream's CDN while the backend has bucket uploads switched off.
   * Rejects on failure so the composer can say so and keep the file staged.
   */
  uploadAttachment: (file: File) => Promise<Attachment>;
  /** Every write on this seam reports the same way: the promise rejects and the
   *  caller says so. Silence would leave the employer believing it landed. */
  setApplicationStatus: (status: ApplicationStatus) => Promise<void>;
  endWithMessage: (text: string, variant: ClosingVariant) => Promise<void>;
  isOwnMessage: (message: Message) => boolean;
  /** The session user, for rules that need the id itself rather than a verdict
   *  about one message — the CTA row asks whether THIS user may click. Read off
   *  the seam so both modes answer from the same place `isOwnMessage` does. */
  currentUserId: string;
}

/**
 * `rewardContext` is what makes a send eligible for the first-reply credit. It
 * is omitted on the end-chat paths deliberately: a closing or rejecting message
 * is not engagement, and paying for one would reward rejecting candidates.
 */
interface RewardContext {
  currentUserId: string;
  counterpartId: string | undefined;
  jobId: string | undefined;
}

/**
 * Push one file to Stream's CDN. `sendImage` and `sendFile` are the same
 * transport with different endpoints; the split only decides how the bubble
 * renders it, so it keys on the browser's MIME type rather than the extension.
 */
async function uploadToStream(raw: MappedChannel['raw'], file: File): Promise<Attachment> {
  const isImage = file.type.startsWith('image/');
  const res = isImage
    ? await raw.sendImage(file, file.name, file.type)
    : await raw.sendFile(file, file.name, file.type);
  const { file: url, thumb_url: thumbUrl } = res as { file?: string; thumb_url?: string };
  if (!url) throw new Error('upload returned no url');
  return {
    type: isImage ? 'image' : 'file',
    url,
    name: file.name,
    // The picked `File` knows its own size, and nothing downstream can recover
    // it: Stream stores whatever the sending client wrote, and computes nothing.
    sizeBytes: file.size,
    // Stream renders this server-side and hands it back on the upload. Only for
    // a document — on an image it is just a smaller copy of the picture, which
    // would make the preview show the same thing twice.
    ...(!isImage && thumbUrl ? { thumbUrl } : {}),
  };
}

/** Our own bucket first; Stream only while the backend has uploads switched off. */
async function uploadFile(raw: MappedChannel['raw'], file: File): Promise<Attachment> {
  const name = uploadName(file);
  let started: ChatFileUploadResponse;
  try {
    started = await startChatFileUpload({
      channelId: raw.cid,
      fileName: name,
      mimeType: file.type,
      sizeBytes: file.size,
    });
  } catch (err) {
    if (err instanceof ApiRequestError && err.code === 'chat_files_off') {
      return uploadToStream(raw, file);
    }
    throw err;
  }
  await postToBucket(started.upload, file);
  return {
    type: started.attachment.type === 'image' ? 'image' : 'file',
    url: '',
    name,
    sizeBytes: file.size,
    fileId: started.fileId,
    wire: started.attachment,
  };
}

/** S3 reads the policy fields before the file, so the file goes last. */
async function postToBucket(upload: ChatFileUploadResponse['upload'], file: File): Promise<void> {
  const form = new FormData();
  for (const [name, value] of Object.entries(upload.fields)) form.append(name, value);
  form.append('file', file);
  const res = await fetch(upload.url, { method: 'POST', body: form });
  if (!res.ok) throw new Error(`bucket upload failed: ${res.status}`);
}

async function sendLive(
  raw: MappedChannel['raw'],
  text: string,
  rewardContext?: RewardContext,
  quotedMessageId?: MessageId,
  attachments?: Attachment[],
  /** A form notice in wire shape, riding the message as the `form` field. */
  form?: Record<string, unknown>,
): Promise<string | undefined> {
  // Judge the reward BEFORE the send: afterwards the first reply is no longer
  // pending, and the whole once-per-channel property rests on this ordering.
  const reward = rewardContext
    ? firstReplyEmitDetail({
        priorMessages: raw.state.messages,
        currentUserId: rewardContext.currentUserId,
        channelId: raw.cid,
        jobseekerId: rewardContext.counterpartId,
        jobId: rewardContext.jobId,
      })
    : null;

  const res = (await raw.sendMessage({
    text,
    ...(form ? { form } : {}),
    ...(quotedMessageId ? { quoted_message_id: quotedMessageId } : {}),
    // Stream's own attachment shape. The upload already happened, so this is
    // just the reference the CDN handed back.
    ...(attachments && attachments.length > 0
      ? {
          attachments: attachments.map((a) => ({
            ...(a.wire ?? {
              ...(a.type === 'image'
                ? { type: 'image', image_url: a.url }
                : { type: 'file', asset_url: a.url }),
              title: a.name,
              // Written here or nowhere. Stream persists what the sender posts, so
              // a field left off now is absent for every reader of this message
              // forever — which is why our own files used to show no size while
              // the candidate's did.
              ...(a.sizeBytes !== undefined ? { file_size: a.sizeBytes } : {}),
              ...(a.thumbUrl ? { thumb_url: a.thumbUrl } : {}),
            }),
            ...(a.voice
              ? {
                  type: 'voiceRecording',
                  duration: a.voice.durationS,
                  waveform_data: a.voice.waveform,
                }
              : {}),
          })),
        }
      : {}),
  })) as { message?: SentMessage } | undefined;
  if (res?.message && isBounced(res.message)) {
    holdBounce(raw.cid, res.message);
    throw new MessageBouncedError();
  }
  const { channelType, channelId } = cidParts(raw.cid);
  // stream-chat parity: the host learns of the employer's outbound message.
  emitToHost('message-sent', {
    messageId: res?.message?.id ?? '',
    text,
    channelId,
    channelType,
    timestamp: res?.message?.created_at ?? new Date().toISOString(),
  });
  // Only after the send actually succeeded — the host awards credits on this.
  if (reward) emitToHost('gamification-action', reward);
  track('message_sent', { channel_id: channelId, channel_type: channelType });
  bump();
  return res?.message?.id;
}

function holdBounce(cid: string, sent: SentMessage): void {
  const bounce = toBounce(sent, cid);
  bounces.set(cacheKey(cid), bounce);
  track('moderation_banner_sent', {
    banner_type: 'inappropriate',
    channel_id: cidParts(cid).channelId,
    message_id: bounce.messageId,
  });
  bump();
}

/** Conversation backed by a watched Stream channel. Reads (messages / ended /
 *  status) come straight off the channel. Sends, stage writes, and the reject
 *  status write persist to the backend live; only the server-side channel close
 *  (end-chat) is still deferred to the follow-up epic. */
function liveConversation(channelId: string): UseConversation {
  const uid = live!.client.userID ?? '';
  const raw =
    openChannels.get(cacheKey(channelId)) ?? live!.channels.find((c) => c.cid === channelId);
  const mapped = raw ? mapChannel(raw, uid) : undefined;
  return {
    messages: raw ? mapMessages(raw, uid) : [],
    bounce: bounces.get(cacheKey(channelId)) ?? null,
    loading: openingChannels.has(channelId),
    sending: false,
    ended: (mapped ? mapped.channel.phase !== 'open' : false) || !!mut.ended[channelId],
    applicationStatus: mut.statuses[channelId] ?? mapped?.channel.applicationStatus ?? 'pending',
    statusOverride: mut.statuses[channelId],
    send: async (text, quotedMessageId, attachments, plain) => {
      // An attachment alone is a complete message, so empty text is only
      // rejected when there is nothing riding with it.
      if (!raw || (text.trim() === '' && !attachments?.length)) return;
      return sendLive(
        raw,
        text,
        {
          currentUserId: uid,
          counterpartId: mapped?.counterpart.id,
          jobId: mapped?.channel.jobMeta?.jobId,
        },
        plain ? undefined : quotedMessageId,
        attachments,
      );
    },
    sendForm: async (form, note) => {
      if (!raw) return;
      // Dispatch first: chat-kit pins the version and hands back the exact
      // `form` payload to post — the client copies, never assembles.
      const dispatch = await dispatchFormApi({ formId: form.id, channelId: raw.cid });
      const messageId = await sendLive(
        raw,
        note.trim(),
        {
          currentUserId: uid,
          counterpartId: mapped?.counterpart.id,
          jobId: mapped?.channel.jobMeta?.jobId,
        },
        undefined,
        undefined,
        dispatch.notice,
      );
      // Best effort: the notice already rides the message, and an unlinked
      // dispatch is chat-kit's documented repair case, not a failed send.
      if (messageId) {
        void linkFormDispatchApi({ dispatchId: dispatch.dispatchId, messageId }).catch(
          () => undefined,
        );
      }
      trackFormSent(cidParts(raw.cid).channelId, form.id, dispatch.dispatchId, false);
    },
    // The thread picks the card up from Stream's message.new, like any other send.
    sendJob: async (jobId) => {
      if (!raw) return;
      await sendJobApi({ channelId, jobId });
    },
    // The thread picks the new text up from Stream's message.updated, like any other change.
    editMessage: (messageId, text) => editMessageApi({ channelId, messageId, text }),
    dismissBounce: () => {
      bounces.delete(cacheKey(channelId));
      bump();
    },
    uploadAttachment: async (file) => {
      if (!raw) throw new Error('no channel');
      return uploadFile(raw, file);
    },
    setApplicationStatus: async (status) => {
      if (WRITE_STATUSES.has(status)) await persistLiveStatus(channelId, status);
    },
    endWithMessage: async (text, variant) => {
      if (text.trim() === '') return;
      // BOTH variants close the channel server-side. The BFF end-chat route sends
      // the closing message as the employer and stamps the channel `closing`,
      // which mapChannel reflects as phase + deleteAt, so the deletion countdown
      // lights up on its own. Reject additionally moves the applicant to
      // `rejected`: upstream keeps end-chat and applicant-status as separate
      // endpoints deliberately, and chat-emp is where they are coupled.
      //
      // Reject used to send the message itself via `sendLive` and never close the
      // channel, which left every job-bounded chat un-stamped — so `delete_at`
      // never arrived and the countdown could not appear on the main employer
      // path. It must NOT send here as well: the backend already sends it, and
      // doing both posts the decline twice.
      const rejecting = variant === 'reject';
      setEndedOverlay(channelId, true);
      try {
        await endChatApi(channelId, text, rejecting);
      } catch (error) {
        setEndedOverlay(channelId, false);
        throw error;
      }
      if (rejecting) await persistLiveStatus(channelId, 'rejected');
      // Analytics is emitted server-side by the end-chat route (authoritative: it
      // fires only if the channel really closed), so only the host event goes out
      // from here — emitting both would double-count. Awaited above for the same
      // reason: the event says the decline was sent, so a close that failed must
      // not report one.
      const { channelType, channelId: id } = cidParts(channelId);
      emitToHost('end-chat-decline-sent', { channelId: id, channelType, alsoRejected: rejecting });
    },
    isOwnMessage: (message) => isEmployerSide(message.senderId),
    currentUserId: uid,
  };
}

export function useConversation(channelId: string | null): UseConversation {
  useSource();
  const liveReady = useLiveReady();

  // Hydrate the open channel, and drop the cache entry when the pane closes or
  // switches. The channel itself stays watched — see `openChannels`.
  useEffect(() => {
    if (!channelId) return;
    void openConversationChannel(channelId);
    return () => {
      closeConversationChannel(channelId);
    };
  }, [channelId]);

  // Having a conversation on screen is what reads it, which is why the read lives
  // here and not on a row click: a deep link is reading too, while a chat that was
  // only listed must not send the candidate a receipt.
  //
  // "On screen" has to be maintained, not just sampled once on open. A message
  // arriving into the thread you are reading is read as well, and marking only at
  // open left it unread until the pane closed and reopened — which on an Info
  // thread, where the employer never replies, means never.
  //
  // A mount is not proof of a human, so every read is gated on the tab being
  // visible: a restored session parked in a background tab reports nothing, and
  // the `visibilitychange` catch-up reads it when someone actually looks.
  useEffect(() => {
    if (!channelId) return;
    let alive = true;

    // `markReadLive` skips a conversation with nothing unread, so the repeats
    // below cost no write.
    const mark = () => {
      if (alive && document.visibilityState === 'visible') void markReadLive(channelId);
    };

    void openConversationChannel(channelId).then(mark);

    const sub = live?.client.on('message.new', (event) => {
      if (event.channel_id === bareId(channelId)) mark();
    });
    document.addEventListener('visibilitychange', mark);

    return () => {
      alive = false;
      sub?.unsubscribe();
      document.removeEventListener('visibilitychange', mark);
    };
    // `liveReady` is what re-runs this once a client installs: without it a
    // conversation mounted before the connect lands would never subscribe.
  }, [channelId, liveReady]);

  if (live && channelId) return liveConversation(channelId);
  const channel = channelId ? allChannels().find((c) => c.id === channelId) : undefined;
  return {
    messages: channelId ? [...(seed[channelId] ?? []), ...(mut.outbox[channelId] ?? [])] : [],
    loading: false,
    sending: false,
    // Ended if the base channel is closing/deleting OR it was ended this session.
    ended: channel ? isEnded(channel) || !!mut.ended[channel.id] : false,
    applicationStatus: channel
      ? (mut.statuses[channel.id] ?? applicationStatusOf(channel))
      : 'pending',
    statusOverride: channel ? mut.statuses[channel.id] : undefined,
    send: async (text, quotedMessageId, _attachments, plain) => {
      if (channelId) send(channelId, text, plain ? undefined : quotedMessageId);
    },
    sendForm: async (form, note) => {
      if (channelId) sendFormFixture(channelId, form, note);
    },
    sendJob: async (jobId) => {
      if (channelId) sendJobFixture(channelId, jobId);
    },
    editMessage: async (messageId, text) => {
      if (channelId) editFixture(channelId, messageId, text);
    },
    // Fixtures never bounce.
    bounce: null,
    dismissBounce: () => {},
    // Fixtures have no Stream channel to upload to. Rejecting is honest: the
    // composer shows the failure rather than staging a file that would silently
    // vanish on send.
    uploadAttachment: async () => {
      throw new Error('attachments need a live channel');
    },
    setApplicationStatus: async (status) => {
      if (channelId) setApplicationStatus(channelId, status);
    },
    endWithMessage: async (text, variant) => {
      if (channelId) endWithMessage(channelId, text, variant);
    },
    isOwnMessage: (message) => isEmployerSide(message.senderId),
    currentUserId: EMPLOYER_ID,
  };
}

export interface ConversationDetails {
  loading: boolean;
  /** The last read failed; the panel offers a retry rather than an empty profile. */
  failed?: boolean;
  /**
   * Fetch again now. The panel's retry affordance calls it; the live
   * `data_changed` announcement does not need it (a revision bump re-runs the
   * effect on its own).
   */
  refetch: () => void;
  profile?: CandidateProfile;
  /**
   * The posting, minus its id — the channel already carries that, derived with
   * the same `jobIdFromChannel` the BFF used, so this enriches the channel's
   * `jobMeta` rather than replacing it.
   */
  jobMeta?: Omit<JobMeta, 'jobId'>;
  appliedAt?: string;
  applicationStatus?: ApplicationStatus;
}

/** Internal: the channel a `ConversationDetails` describes, so a switch can be
 *  detected during render rather than one frame later, in the effect.
 *
 *  `refetch` is omitted deliberately — it belongs to the hook, not to a fetch
 *  result, and holding it in state would mean writing it into every setState. */
interface KeyedDetails extends Omit<ConversationDetails, 'refetch'> {
  id: string | null;
}

function toProfile(d: ConversationDetailsDto['candidate']): CandidateProfile {
  return {
    ...(d.gender ? { gender: d.gender } : {}),
    ...(d.age != null ? { age: d.age } : {}),
    ...(d.domicile ? { domicile: d.domicile } : {}),
    preferredLocations: d.preferredLocations ?? [],
    experienceYears: d.experienceYears,
    experiences: d.experiences,
    skills: d.skills,
    languages: d.languages,
    ...(d.expectedSalary ? { expectedSalary: d.expectedSalary } : {}),
    ...(d.education ? { education: d.education } : {}),
    ...(d.nationality ? { nationality: d.nationality } : {}),
    ...(d.contact ? { contact: d.contact } : {}),
    ...(d.resume ? { resume: d.resume } : {}),
    unlocked: d.unlocked,
  };
}

/** Live candidate/application/job for the open conversation. Fixtures mode
 *  returns nothing — the InboxItem already carries fixture data — so this only
 *  fetches when a real Stream client is installed. */
export function useConversationDetails(channelId: string | null): ConversationDetails {
  useSource();
  const [state, setState] = useState<KeyedDetails>({ loading: false, id: null });
  // Bumped by the backend's `data_changed`; bumped locally by `refetch`. Both
  // land on the same effect dependency, so there is one fetch path and not two.
  const announced = channelId ? (detailsRevisions.get(channelId) ?? 0) : 0;
  const [manual, setManual] = useState(0);
  const refetch = useCallback(() => setManual((n) => n + 1), []);

  useEffect(() => {
    if (!channelId || !live) {
      setState({ loading: false, id: channelId });
      return;
    }
    let alive = true;
    // `loading` only on the FIRST read of a channel. A refetch triggered by an
    // announcement must not blank a panel the employer is reading — it would
    // flash every section away to deliver two new rows. Keep the old data on
    // screen until the new data replaces it.
    setState((prev) =>
      prev.id === channelId ? { ...prev, id: channelId } : { loading: true, id: channelId },
    );
    getConversationDetails(channelId)
      .then((d) => {
        if (!alive) return;
        const profile = toProfile(d.candidate);
        setUnlockHint(channelId, d.candidate.unlocked);
        bump();
        setState({
          loading: false,
          id: channelId,
          profile,
          ...(d.job
            ? {
                jobMeta: {
                  jobTitle: d.job.title,
                  jobType: d.job.jobType,
                  ...(d.job.location ? { jobLocation: d.job.location } : {}),
                  ...(d.job.description ? { description: d.job.description } : {}),
                  ...(d.job.responsibilities ? { responsibilities: d.job.responsibilities } : {}),
                  ...(d.job.requirements ? { requirements: d.job.requirements } : {}),
                },
              }
            : {}),
          ...(d.application
            ? {
                appliedAt: d.application.appliedAt,
                // The DTO's 'new' is the channel-level 'pending' ("not yet marked").
                applicationStatus:
                  d.application.status === 'new' ? 'pending' : d.application.status,
              }
            : {}),
        });
      })
      .catch(() => {
        // Panel degrades to its empty note; the header falls back to the
        // channel's own status rather than skeletoning forever.
        if (alive) setState({ loading: false, id: channelId, failed: true });
      });
    return () => {
      alive = false;
    };
  }, [channelId, announced, manual]);

  // The effect sets `loading` a frame late, so a channel switch would otherwise
  // report "loaded" while still holding the PREVIOUS channel's details — long
  // enough for the header to draw the wrong hiring stage. Derive the switch here.
  const switching: KeyedDetails = { loading: Boolean(channelId && live), id: channelId };
  return { ...(state.id === channelId ? state : switching), refetch };
}
